import { usePlan } from "../billing/PlanProvider";
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { messages } from "../i18n/messages";
import { StandardModal } from "./StandardModal";
import { FainanceColorSelector } from "./FainanceColorSelector";

export type DashboardWidgetSize = "small" | "medium" | "large" | "full";
export type DashboardWidgetHeight = "compact" | "normal" | "tall";

type TranslationKey = keyof typeof messages.it;

export interface DashboardWidgetDefinition {
  id: string;
  title: string;
  titleIcon?: ReactNode;
  titleKey?: TranslationKey;
  defaultSize?: DashboardWidgetSize;
  defaultLayoutVersion?: number;
  defaultHeight?: DashboardWidgetHeight;
  defaultColor?: string;
  defaultOpacity?: number;
  defaultHidden?: boolean;
  defaultShowTitle?: boolean;
  premium?: boolean;
  groupId?: string;
  groupTitle?: string;
  groupSubtitle?: string;
  groupIcon?: ReactNode;
  groupColor?: string;
  groupOrder?: number;
  content: ReactNode;
}

interface DashboardWidgetConfig {
  id: string;
  title: string;
  size: DashboardWidgetSize;
  height: DashboardWidgetHeight;
  color: string;
  opacity: number;
  hidden: boolean;
  customTitle: boolean;
  showTitle?: boolean;
  layoutVersion?: number;
}


function clampOpacity(value: unknown): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 100;
  return Math.max(20, Math.min(100, Math.round(parsed)));
}

function hexToRgba(hex: string, opacity: number): string {
  const normalized = /^#[0-9a-f]{6}$/i.test(hex) ? hex : "#ffffff";
  const r = parseInt(normalized.slice(1, 3), 16);
  const g = parseInt(normalized.slice(3, 5), 16);
  const b = parseInt(normalized.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${clampOpacity(opacity) / 100})`;
}

function initialConfig(definitions: DashboardWidgetDefinition[], storageKey: string): DashboardWidgetConfig[] {
  const defaults = definitions.map((widget) => ({
    id: widget.id,
    title: widget.title,
    size: widget.defaultSize || "medium",
    layoutVersion: widget.defaultLayoutVersion,
    height: widget.defaultHeight || "normal",
    color: widget.defaultColor || "#ffffff",
    opacity: clampOpacity(widget.defaultOpacity ?? 100),
    hidden: Boolean(widget.defaultHidden),
    customTitle: false,
    showTitle: widget.defaultShowTitle !== false
  } satisfies DashboardWidgetConfig));

  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return defaults;
    const saved = JSON.parse(raw) as DashboardWidgetConfig[];
    if (!Array.isArray(saved)) return defaults;
    const savedMap = new Map(saved.map((item) => [item.id, item]));
    const ordered: DashboardWidgetConfig[] = [];
    saved.forEach((item) => {
      const base = defaults.find((candidate) => candidate.id === item.id);
      if (!base) return;
      const definition = definitions.find((candidate) => candidate.id === item.id);
      const translatedDefaults = definition?.titleKey
        ? [messages.it[definition.titleKey], messages.en[definition.titleKey], messages.es[definition.titleKey]]
        : [base.title];
      const savedTitle = typeof item.title === "string" ? item.title.trim() : "";
      const customTitle = typeof item.customTitle === "boolean"
        ? item.customTitle
        : Boolean(savedTitle && !translatedDefaults.includes(savedTitle));
      ordered.push({
        ...base,
        ...item,
        title: customTitle && savedTitle ? savedTitle : base.title,
        size: definition?.defaultLayoutVersion && item.layoutVersion !== definition.defaultLayoutVersion ? base.size : ["small", "medium", "large", "full"].includes(item.size) ? item.size : base.size,
        layoutVersion: definition?.defaultLayoutVersion,
        height: ["compact", "normal", "tall"].includes(item.height) ? item.height : base.height,
        color: /^#[0-9a-f]{6}$/i.test(item.color || "") ? item.color : base.color,
        opacity: clampOpacity(item.opacity),
        hidden: Boolean(item.hidden),
        customTitle,
        showTitle: typeof item.showTitle === "boolean" ? item.showTitle : base.showTitle
      });
    });
    // One-time migration: the old default "Alloggi" widget was visible in saved
    // dashboards even after its default became hidden. Do not reset any other widget,
    // and do not hide it again after the user explicitly re-enables it.
    if (storageKey.startsWith("tripai.trip.dashboard.") && defaults.some(item => item.id === "stops" && item.hidden)) {
      const marker = `${storageKey}.stays-default-hidden-v1`;
      if (localStorage.getItem(marker) !== "1") {
        const oldStay = ordered.find(item => item.id === "stops");
        if (oldStay) {
          oldStay.hidden = true;
          localStorage.setItem(storageKey, JSON.stringify(ordered));
        }
        localStorage.setItem(marker, "1");
      }
    }
    // Place the two new widgets after Checklist without resetting personalized Home layouts.
    const incomingContext = defaults.filter((item) => (item.id === "contextSuggest" || item.id === "contextReplan") && !savedMap.has(item.id));
    const checklistPosition = ordered.findIndex((item) => item.id === "itineraryOverview");
    if (checklistPosition >= 0) ordered.splice(checklistPosition + 1, 0, ...incomingContext);
    defaults.forEach((item) => { if (!savedMap.has(item.id) && !(checklistPosition >= 0 && incomingContext.some((newItem) => newItem.id === item.id))) ordered.push(item); });
    if (storageKey.startsWith("tripai.home.dashboard.")) {
      const marker = storageKey + ".share-layout-v161";
      if (localStorage.getItem(marker) !== "1") {
        const share = ordered.findIndex(item => item.id === "shareTrip");
        const current = ordered.findIndex(item => item.id === "nextTrip");
        if (share >= 0 && current >= 0 && share < current) [ordered[share], ordered[current]] = [ordered[current], ordered[share]];
        const card = ordered.find(item => item.id === "shareTrip");
        if (card) { card.size = "full"; if (!card.customTitle) card.showTitle = false; }
        localStorage.setItem(storageKey, JSON.stringify(ordered));
        localStorage.setItem(marker, "1");
      }
    }
    return ordered;
  } catch {
    return defaults;
  }
}

export function ModularDashboard({
  storageKey,
  widgets,
  editing: controlledEditing,
  onEditingChange,
  showEditButton = true
}: {
  storageKey: string;
  widgets: DashboardWidgetDefinition[];
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
  showEditButton?: boolean;
}) {
  const { complete } = usePlan();
  const { t } = useI18n();
  const [internalEditing, setInternalEditing] = useState(false);
  const editing = typeof controlledEditing === "boolean" ? controlledEditing : internalEditing;

  function setEditing(next: boolean | ((current: boolean) => boolean)) {
    const resolved = typeof next === "function" ? next(editing) : next;
    if (typeof controlledEditing === "boolean") onEditingChange?.(resolved);
    else setInternalEditing(resolved);
  }
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<DashboardWidgetConfig | null>(null);
  function openWidgetEditor(config: DashboardWidgetConfig) {
    setEditDraft({ ...config });
    setSelectedId(config.id);
  }
  function closeWidgetEditor() { setSelectedId(null); setEditDraft(null); }
  function updateDraft(patch: Partial<DashboardWidgetConfig>) {
    setEditDraft(current => current ? { ...current, ...patch } : null);
  }
  function saveWidgetDraft() {
    if (!editDraft) return;
    updateConfig(editDraft.id, { title:editDraft.title, size:editDraft.size, height:editDraft.height,
      color:editDraft.color, opacity:editDraft.opacity, hidden:editDraft.hidden,
      customTitle:editDraft.customTitle, showTitle:editDraft.showTitle });
    closeWidgetEditor();
  }
  const [configs, setConfigs] = useState<DashboardWidgetConfig[]>(() => initialConfig(widgets, storageKey));

  const widgetTitleSignature = widgets.map((widget) => `${widget.id}:${widget.title}`).join("|");

  useEffect(() => {
    setConfigs(initialConfig(widgets, storageKey));
  }, [storageKey, widgetTitleSignature]);

  const definitions = useMemo(() => new Map(widgets.map((widget) => [widget.id, widget])), [widgets]);
  const selected = selectedId ? configs.find((item) => item.id === selectedId) : undefined;

  function updateConfig(id: string, patch: Partial<DashboardWidgetConfig>) {
    setConfigs((current) => {
      const next = current.map((item) => item.id === id ? { ...item, ...patch } : item);
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  }

  function move(id: string, direction: -1 | 1) {
    setConfigs((current) => {
      const siblingIds = current
        .filter((item) => definitions.has(item.id))
        .map((item) => item.id);
      const siblingIndex = siblingIds.indexOf(id);
      const targetSiblingIndex = siblingIndex + direction;
      if (siblingIndex < 0 || targetSiblingIndex < 0 || targetSiblingIndex >= siblingIds.length) return current;
      const targetId = siblingIds[targetSiblingIndex];
      const index = current.findIndex((item) => item.id === id);
      const target = current.findIndex((item) => item.id === targetId);
      if (index < 0 || target < 0) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  }

  function resetAll() {
    const next = widgets.map((widget) => ({
      id: widget.id,
      title: widget.title,
      size: widget.defaultSize || "medium",
    layoutVersion: widget.defaultLayoutVersion,
      height: widget.defaultHeight || "normal",
      color: widget.defaultColor || "#ffffff",
      opacity: clampOpacity(widget.defaultOpacity ?? 100),
      hidden: Boolean(widget.defaultHidden),
      customTitle: false,
      showTitle: widget.defaultShowTitle !== false
    } satisfies DashboardWidgetConfig));
    setConfigs(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
  }

  // Group only consecutive cards. A move must never be undone by a group sort.
  const renderGroups: Array<{id:string; groupId?:string; configs:DashboardWidgetConfig[]; title?:string; subtitle?:string; icon?:ReactNode; color?:string}> = [];
  configs.forEach(config => {
    const definition=definitions.get(config.id);if(!definition)return;
    const previous=renderGroups[renderGroups.length-1];
    if(definition.groupId && previous?.groupId===definition.groupId)previous.configs.push(config);
    else renderGroups.push({id:definition.groupId?`${definition.groupId}:${config.id}`:`__single__${config.id}`,groupId:definition.groupId,configs:[config],title:definition.groupTitle,subtitle:definition.groupSubtitle,icon:definition.groupIcon,color:definition.groupColor});
  });

  function renderWidget(config: DashboardWidgetConfig) {
    const definition = definitions.get(config.id);
    if (!definition || (config.hidden && !editing)) return null;
    const siblingConfigs = configs.filter((item) => definitions.has(item.id));
    const siblingIndex = siblingConfigs.findIndex((item) => item.id === config.id);
    // Day worklets must remain legible also on user-customized backgrounds/opacity.
    // Contrast is decided from the effective RGB surface (including transparency).
    const safeHex = /^#[0-9a-f]{6}$/i.test(config.color) ? config.color : "#FFFFFF";
    const channel = (index: number) => parseInt(safeHex.slice(index, index + 2), 16);
    const opacity = clampOpacity(config.opacity) / 100;
    const night = typeof document !== "undefined" && document.documentElement.getAttribute("data-trip-theme") === "dark";
    const canvas = night ? [14, 32, 31] : [247, 245, 239];
    const rgb = [channel(1), channel(3), channel(5)].map((value, index) => value * opacity + canvas[index] * (1 - opacity));
    const linear = (value: number) => { const s = value / 255; return s <= .04045 ? s / 12.92 : ((s + .055) / 1.055) ** 2.4; };
    const luminance = rgb.reduce((sum, value, index) => sum + linear(value) * [0.2126, 0.7152, 0.0722][index], 0);
    const dayForeground = (1.05 / (luminance + 0.05)) > ((luminance + 0.05) / 0.077) ? "#F1F5F1" : "#093232";
    const style = { "--widget-bg": hexToRgba(config.color, config.opacity), "--mundely-day-foreground": dayForeground } as CSSProperties;
    return (
      <article key={config.id} data-widget={config.id} className={`modular-widget size-${config.size} height-${config.height}${config.hidden ? " hidden-widget" : ""}`} style={style}>
        <header className="modular-widget-header">
          {config.showTitle !== false && definition.titleIcon && <span className="modular-widget-title-icon" aria-hidden="true">{definition.titleIcon}</span>}
          {config.showTitle !== false && <h2>{config.title}</h2>}
          {editing && (
            <div className="modular-widget-controls">
              <button type="button" disabled={siblingIndex === 0} onClick={() => move(config.id, -1)} aria-label={t("dashboard.moveUp")} title={t("dashboard.moveUp")}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 14 6-6 6 6" /></svg></button>
              <button type="button" disabled={siblingIndex === siblingConfigs.length - 1} onClick={() => move(config.id, 1)} aria-label={t("dashboard.moveDown")} title={t("dashboard.moveDown")}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 10 6 6 6-6" /></svg></button>
              <button type="button" className="worklet-edit-pencil" onClick={() => openWidgetEditor(config)} aria-label={t("dashboard.editWidget")} title={t("dashboard.editWidget")}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h4l11-11-4-4L4 16z"/><path d="m13.5 6.5 4 4" /></svg></button>
            </div>
          )}
        </header>
        <div className="modular-widget-body">{definition.premium && !complete ? <button type="button" className="trip-hub-premium-tile" onClick={() => window.dispatchEvent(new Event("mundely:open-plan"))}><span aria-hidden="true">🔒</span><strong>{t(definition.groupId === "organize" ? "billing.completePlanLabel" : "billing.completeRequired")}</strong></button> : definition.content}</div>
      </article>
    );
  }

  return (
    <section className={editing ? "modular-dashboard editing grouped-layout" : "modular-dashboard grouped-layout"}>
      {(showEditButton || editing) && <div className="modular-dashboard-toolbar">
        {showEditButton && <button className={editing ? "modular-dashboard-edit active" : "modular-dashboard-edit"} type="button" onClick={() => setEditing((value) => !value)} aria-label={editing ? t("dashboard.done") : t("dashboard.customize")} title={editing ? t("dashboard.done") : t("dashboard.customize")}>
          {editing ? (
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.83 2.83-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21H9.6v-.1A1.7 1.7 0 0 0 8.55 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06-2.83-2.83.06-.06A1.7 1.7 0 0 0 4.2 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H2.4V9.6h.1A1.7 1.7 0 0 0 4.2 8.55a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.83-2.83.06.06A1.7 1.7 0 0 0 8.55 4.2a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V2.4h4v.1A1.7 1.7 0 0 0 15 4.2a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.83 2.83-.06.06A1.7 1.7 0 0 0 19.4 8.55a1.7 1.7 0 0 0 .6 1 1.7 1.7 0 0 0 1.1.4h.1v4h-.1a1.7 1.7 0 0 0-1.7 1.05Z"/></svg>
          )}
        </button>}
        {editing && <button className="modular-dashboard-reset" type="button" onClick={resetAll}>{t("dashboard.restoreDefaults")}</button>}
        {editing && !showEditButton && <button className="modular-dashboard-done" type="button" onClick={() => setEditing(false)}>{t("dashboard.done")}</button>}
      </div>}

      <div className="modular-dashboard-grid">
        {renderGroups.map((group) => {
          const rendered = group.configs.map(renderWidget).filter(Boolean);
          if (!rendered.length) return null;
          if (group.id.startsWith("__single__")) return rendered[0];
          return (
            <section key={group.id} className="modular-widget-group" data-group={group.groupId} style={{ "--widget-group-bg": group.color || "#ffffff" } as CSSProperties}>
              <header className="modular-widget-group-header">
                {group.icon && <span className="modular-widget-group-icon" aria-hidden="true">{group.icon}</span>}
                <div><h2>{group.title}</h2>{group.subtitle && <p>{group.subtitle}</p>}</div>
              </header>
              <div className="modular-widget-group-grid">{rendered}</div>
            </section>
          );
        })}
      </div>

      {editing && <button type="button" className="button primary dashboard-finish-floating" onClick={() => { closeWidgetEditor(); setEditing(false); }}>{t("dashboard.done")}</button>}

      {selected && editDraft && (
        <StandardModal
          title={selected.title}
          eyebrow={t("dashboard.editWidget")}
          body={definitions.get(selected.id)?.groupSubtitle || undefined}
          titleAside={definitions.get(selected.id)?.titleIcon ? <span className="mundely-worklet-editor-title-icon" aria-hidden="true">{definitions.get(selected.id)?.titleIcon}</span> : undefined}
          onClose={closeWidgetEditor}
          className="mundely-widget-editor-modal">
          <div className="dashboard-widget-settings fainance-worklet-editor">
            <div className="fainance-worklet-field">
              <span>{t("dashboard.showTitle")}</span>
              <div className="fainance-worklet-segmented" role="group" aria-label={t("dashboard.showTitle")}>
                <button type="button" aria-pressed={editDraft.showTitle !== false} onClick={() => updateDraft({ showTitle:true })}>{t("dashboard.showTitle")}</button>
                <button type="button" aria-pressed={editDraft.showTitle === false} onClick={() => updateDraft({ showTitle:false })}>{t("dashboard.hideTitle")}</button>
              </div>
            </div>
            <label className="fainance-worklet-field"><span>{t("dashboard.customTitle")}</span>
              <input className="input" value={editDraft.customTitle ? editDraft.title : ""} placeholder={selected.title}
                onChange={(event) => updateDraft({ title:event.target.value || selected.title, customTitle:Boolean(event.target.value) })} />
            </label>
            <div className="fainance-worklet-field">
              <span>{t("dashboard.widgetSize")}</span>
              <div className="fainance-worklet-segmented fainance-worklet-size-segmented" role="group" aria-label={t("dashboard.widgetSize")}>
                {([['small','0.5x'],['medium','1x'],['full','2x']] as const).map(([size,label]) =>
                  <button key={size} type="button" aria-pressed={editDraft.size === size || (size === "full" && editDraft.size === "large")}
                    onClick={() => updateDraft({size})}>{label}</button>)}
              </div>
            </div>
            <label className="fainance-worklet-field fainance-worklet-height">
              <span>{t("dashboard.height")}</span>
              <select className="input" value={editDraft.height}
                onChange={(event) => updateDraft({height:event.target.value as DashboardWidgetHeight})}>
                <option value="compact">{t("dashboard.height.compact")}</option>
                <option value="normal">{t("dashboard.height.normal")}</option>
                <option value="tall">{t("dashboard.height.tall")}</option>
              </select>
            </label>
            <div className="fainance-worklet-field">
              <span>{t("dashboard.background")}</span>
              <FainanceColorSelector expanded value={editDraft.color} onChange={(color) => updateDraft({color})} />
            </div>
            <label className="dashboard-opacity fainance-worklet-opacity">
              <span>{t("dashboard.opacity")}</span>
              <input type="range" min="20" max="100" step="5" value={editDraft.opacity} onChange={(event) => updateDraft({opacity:Number(event.target.value)})}/>
              <strong>{`${editDraft.opacity}%`}</strong>
            </label>
            <div className="fainance-worklet-footer">
              <button type="button" className="button secondary fainance-worklet-remove" onClick={() => { updateConfig(editDraft.id, {hidden:true}); closeWidgetEditor(); }}>{t("dashboard.removeFromHome")}</button>
              <div className="fainance-worklet-footer-actions">
                <button type="button" className="button secondary" onClick={closeWidgetEditor}>{t("common.cancel")}</button>
                <button type="button" className="button primary" onClick={saveWidgetDraft}>{t("dashboard.saveSettings")}</button>
              </div>
            </div>
          </div>
        </StandardModal>
      )}
    </section>
  );
}
